'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

export default function FlightSearchForm() {
  const router = useRouter();
  const [tripType, setTripType] = useState<'roundtrip' | 'oneway' | 'multicity'>('roundtrip');
  const [cabinClass, setCabinClass] = useState('Economy');
  const [travelers, setTravelers] = useState({ adults: 1, children: 0, infants: 0 });
  const [showTravelerPicker, setShowTravelerPicker] = useState(false);
  const [flexDates, setFlexDates] = useState(false);
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [departDate, setDepartDate] = useState('');
  const [returnDate, setReturnDate] = useState('');

  const totalTravelers = travelers.adults + travelers.children + travelers.infants;

  const handleSearch = () => {
    // BACKEND: POST /api/search/flights with { origin, destination, departDate, returnDate, travelers, cabinClass, tripType }
    router.push('/search-results-page');
  };

  const adjustTraveler = (type: keyof typeof travelers, delta: number) => {
    setTravelers((prev) => ({
      ...prev,
      [type]: Math.max(type === 'adults' ? 1 : 0, Math.min(9, prev[type] + delta)),
    }));
  };

  return (
    <div className="space-y-4">
      {/* Trip Type + Cabin Class Row */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {(['roundtrip', 'oneway', 'multicity'] as const).map((type) => (
            <button
              key={`trip-${type}`}
              onClick={() => setTripType(type)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-150 capitalize ${
                tripType === type
                  ? 'bg-card text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {type === 'multicity' ? 'Multi-city' : type === 'roundtrip' ? 'Round trip' : 'One way'}
            </button>
          ))}
        </div>

        <select
          value={cabinClass}
          onChange={(e) => setCabinClass(e.target.value)}
          className="input-base px-3 py-1.5 text-xs font-semibold pr-7 cursor-pointer"
        >
          {['Economy', 'Premium Economy', 'Business', 'First'].map((c) => (
            <option key={`cabin-${c}`}>{c}</option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={flexDates}
            onChange={(e) => setFlexDates(e.target.checked)}
            className="checkbox-custom"
          />
          <span>Flexible dates ±3 days</span>
        </label>
      </div>

      {/* Main Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Origin */}
        <div className="relative">
          <label className="block text-xs font-600 text-muted-foreground mb-1">From</label>
          <div className="relative">
            <Icon name="PaperAirplaneIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary pointer-events-none" />
            <input
              type="text"
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              placeholder="City or airport"
              className="input-base w-full pl-9 pr-3 py-3 text-sm"
            />
          </div>
        </div>

        {/* Destination */}
        <div className="relative">
          <label className="block text-xs font-600 text-muted-foreground mb-1">To</label>
          <div className="relative">
            <Icon name="MapPinIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-accent pointer-events-none" />
            <input
              type="text"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="City or airport"
              className="input-base w-full pl-9 pr-3 py-3 text-sm"
            />
          </div>
        </div>

        {/* Dates */}
        <div>
          <label className="block text-xs font-600 text-muted-foreground mb-1">
            {tripType === 'roundtrip' ? 'Depart — Return' : 'Departure Date'}
          </label>
          {tripType === 'roundtrip' ? (
            <div className="flex items-center gap-1">
              <input
                type="date"
                value={departDate}
                onChange={(e) => setDepartDate(e.target.value)}
                className="input-base w-full px-2 py-3 text-sm"
              />
              <span className="text-muted-foreground text-xs">→</span>
              <input
                type="date"
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
                className="input-base w-full px-2 py-3 text-sm"
              />
            </div>
          ) : (
            <input
              type="date"
              value={departDate}
              onChange={(e) => setDepartDate(e.target.value)}
              className="input-base w-full px-3 py-3 text-sm"
            />
          )}
        </div>

        {/* Travelers */}
        <div className="relative">
          <label className="block text-xs font-600 text-muted-foreground mb-1">Travelers</label>
          <button
            type="button"
            onClick={() => setShowTravelerPicker(!showTravelerPicker)}
            className="input-base w-full px-3 py-3 text-sm text-left flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              <Icon name="UserGroupIcon" size={16} className="text-muted-foreground" />
              <span>
                {totalTravelers} Traveler{totalTravelers !== 1 ? 's' : ''}, {cabinClass}
              </span>
            </span>
            <Icon name="ChevronDownIcon" size={14} className="text-muted-foreground" />
          </button>

          {showTravelerPicker && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-card-lg z-20 p-4 space-y-3">
              {(
                [
                  { key: 'adults', label: 'Adults', sub: '18+' },
                  { key: 'children', label: 'Children', sub: '2–17' },
                  { key: 'infants', label: 'Infants', sub: 'Under 2' },
                ] as const
              ).map(({ key, label, sub }) => (
                <div key={`traveler-${key}`} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{label}</p>
                    <p className="text-xs text-muted-foreground">{sub}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => adjustTraveler(key, -1)}
                      className="w-7 h-7 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors disabled:opacity-40"
                      disabled={key === 'adults' ? travelers[key] <= 1 : travelers[key] <= 0}
                    >
                      <Icon name="MinusIcon" size={14} />
                    </button>
                    <span className="text-sm font-semibold w-4 text-center tabular-nums">
                      {travelers[key]}
                    </span>
                    <button
                      type="button"
                      onClick={() => adjustTraveler(key, 1)}
                      className="w-7 h-7 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors"
                    >
                      <Icon name="PlusIcon" size={14} />
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setShowTravelerPicker(false)}
                className="btn-primary w-full py-2 text-sm mt-2"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Search Button */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSearch}
          className="btn-primary px-8 py-3 text-sm flex items-center gap-2"
        >
          <Icon name="MagnifyingGlassIcon" size={18} />
          Search Flights
        </button>
      </div>
    </div>
  );
}